"use client";

import { useParams, usePathname } from "next/navigation";
import { FC, memo, MouseEvent, useCallback } from "react";
import { ISportFeedResponse } from "@finbackoffice/websocket-client";
import { RouteNames } from "utils/constants";
import {
    useDefaultLiveParamsFromUrl,
    useDefaultPrematchParamsFromUrl,
    useDefaultSportResultParamsFromUrl,
} from "hooks";
import { NavigationBarItem } from "components/base/navigation/NavigationBarItem";

interface IProps {
    sport: ISportFeedResponse;
    active?: boolean;
    isSelected?: boolean;
    onClickHandler?: (sportId: number) => void;
    hasFavorite: boolean;
    type?: "link" | "button";
    variant?: "base" | "sidebar";
}

const Sport: FC<IProps> = ({
    sport,
    active,
    isSelected,
    onClickHandler,
    hasFavorite,
    variant = "base",
    type = "link",
}) => {
    const pathname = usePathname();
    const params = useParams();
    const { sportSlug: sportSlugFromLiveGameRoutes } = useDefaultLiveParamsFromUrl();
    const { sportSlug: sportSlugFromPrematchGameRoutes } = useDefaultPrematchParamsFromUrl();
    const { sportSlug: sportSlugFromSportResultsGameRoutes } = useDefaultSportResultParamsFromUrl();

    const onClick = useCallback(
        (event: MouseEvent<HTMLAnchorElement | HTMLDivElement>) => {
            if (onClickHandler) {
                event.stopPropagation();
                event.preventDefault();
                onClickHandler(sport.id);
            }
        },
        [onClickHandler, sport],
    );

    const getLink = useCallback(() => {
        let pathReplaced = `${pathname
            ?.replaceAll(
                `/${RouteNames.LIVE}/${RouteNames.TOP}`,
                `/${RouteNames.LIVE}/${RouteNames.ALL}`,
            )
            .replaceAll(`/${RouteNames.FAVORITES}`, `/${RouteNames.ALL}`)}`;

        const sportSlug =
            sportSlugFromPrematchGameRoutes ||
            sportSlugFromLiveGameRoutes ||
            sportSlugFromSportResultsGameRoutes;

        if (!sportSlug) {
            return `${pathReplaced}/${sport.slug}`;
        }

        if (params.prematchGameRoutes) {
            pathReplaced = pathReplaced.replaceAll(
                (params.prematchGameRoutes as string[]).join("/"),
                "",
            );

            return `${pathReplaced}/${sport.slug}`;
        } else if (params.liveGameRoutes) {
            pathReplaced = pathReplaced.replaceAll(
                (params.liveGameRoutes as string[]).join("/"),
                "",
            );

            return `${pathReplaced}/${sport.slug}`;
        } else if (params.resultsGameRoutes) {
            pathReplaced = pathReplaced.replaceAll(
                (params.resultsGameRoutes as string[]).join("/"),
                "",
            );

            return `${pathReplaced}/${sport.slug}`;
        } else {
            const pathSegments = pathReplaced.split("/");
            pathSegments[pathSegments.length - 1] = sport.slug;
            return pathSegments.join("/");
        }
    }, [
        params.liveGameRoutes,
        params.prematchGameRoutes,
        params.resultsGameRoutes,
        pathname,
        sport.slug,
        sportSlugFromLiveGameRoutes,
        sportSlugFromPrematchGameRoutes,
        sportSlugFromSportResultsGameRoutes,
    ]);

    return (
        <NavigationBarItem
            type={type}
            active={Boolean(active)}
            hasFavorite={hasFavorite}
            showGamesCount
            count={sport.games_count}
            sportIconId={sport.id}
            text={sport.name || ""}
            isSelected={Boolean(isSelected)}
            variant={variant}
            onClick={onClick}
            href={getLink()}
        />
    );
};

export default memo(Sport);
